const FOX_ESS_CONTROLLED_URL_PATTERN =
  /^(\/|https?:\/\/([^/]+\.)?(greenlightcity\.co\.za|supabase\.co|netlify\.app)\b)/i;

const getControlledUrl = (value, fallback) => {
  const trimmed = String(value || '').trim();
  if (trimmed && FOX_ESS_CONTROLLED_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }
  return fallback;
};

const foxEssBatteryCatalog = {
  ep6: {
    id: 5,
    name: 'Fox ESS EP6',
    brand: 'foxess',
    price: 'Request quote',
    category: 'battery',
    category_label: 'High Voltage Storage Battery',
    range_label: 'Energy range',
    power_range: '5.76-23.04 kWh',
    description:
      'Compact high-voltage storage battery with modular expansion for scalable residential backup and self-consumption.',
    image_url: '/assets/products/fox-ess/ep6.webp',
    datasheet_url: 'https://www.fox-ess.com/wp-content/uploads/2025/12/EN-EP6-Datasheet-V1.1-20251128.pdf',
    product_url: 'https://www.fox-ess.com/products/battery/24',
    hero_meta: 'IP65 / Expandable Storage',
    card_features: ['High Voltage', 'Expandable System', 'IP65 Rated', 'Easy Install'],
    detail_features: [
      {
        label: 'High Voltage',
        description: 'High-voltage architecture keeps residential storage compact and efficient.',
      },
      {
        label: 'Expandable System',
        description: 'Scale from 5.76 kWh up to 23.04 kWh as the site grows.',
      },
      {
        label: 'IP65 Rated',
        description: 'IP65 protection supports clean indoor or outdoor installation points.',
      },
      {
        label: 'Easy Install',
        description: 'Compact floor or wall-mounted format keeps installation straightforward.',
      },
    ],
    quick_specs: [
      'High-voltage storage battery',
      'Scalable from 5.76 kWh to 23.04 kWh',
      'Up to 4 units in parallel',
      'Floor or wall mounting',
      'IP65 protection',
    ],
  },
  ep11: {
    id: 6,
    name: 'Fox ESS EP11',
    brand: 'foxess',
    price: 'Request quote',
    category: 'battery',
    category_label: 'High Voltage Storage Battery',
    range_label: 'Energy range',
    power_range: '10.36-41.60 kWh',
    description:
      'Scalable high-voltage storage battery sized for larger residential systems and light commercial backup requirements.',
    image_url: '/assets/products/fox-ess/fox-ess-ep11-battery.svg',
    datasheet_url: 'https://www.fox-ess.com/wp-content/uploads/2025/12/EN-EP11-Datasheet-V1.5-20251124.pdf',
    product_url: 'https://www.fox-ess.com/products/battery/23',
    hero_meta: 'IP65 / Expandable Storage',
    card_features: ['High Voltage', 'Expandable System', 'IP65 Rated', 'Easy Install'],
    detail_features: [
      {
        label: 'High Voltage',
        description: 'High-voltage storage design suits larger residential and light commercial systems.',
      },
      {
        label: 'Expandable System',
        description: 'Scale from 10.36 kWh up to 41.60 kWh as storage demand increases.',
      },
      {
        label: 'IP65 Rated',
        description: 'IP65 protection supports reliable indoor or outdoor placement.',
      },
      {
        label: 'Easy Install',
        description: 'Floor or wall-mounted format keeps larger storage additions tidy and practical.',
      },
    ],
    quick_specs: [
      'High-voltage storage battery',
      'Scalable from 10.36 kWh to 41.60 kWh',
      'Up to 4 units in parallel',
      'Floor or wall mounting',
      'IP65 protection',
    ],
  },
};

const FOX_ESS_BATTERY_KEYS = ['ep6', 'ep11'];

const isFoxEssBattery = (product) =>
  String(product?.brand || '').toLowerCase() === 'foxess' &&
  String(product?.category || '').toLowerCase() === 'battery';

const getFoxEssBatteryKey = (product) => {
  const haystack = `${product?.id || ''} ${product?.name || ''} ${product?.power_range || ''}`.toLowerCase();
  if (haystack.includes('ep6')) return 'ep6';
  if (haystack.includes('ep11')) return 'ep11';
  return null;
};

const buildFoxEssBatteryProduct = (key, currentProduct = null) => {
  const source = foxEssBatteryCatalog[key];
  return {
    ...currentProduct,
    ...source,
    image_url: getControlledUrl(currentProduct?.image_url, source.image_url),
    datasheet_url: getControlledUrl(currentProduct?.datasheet_url, source.datasheet_url),
    price: currentProduct?.price || source.price,
  };
};

export const FOX_ESS_BATTERY_PRODUCTS = FOX_ESS_BATTERY_KEYS.map((key) =>
  buildFoxEssBatteryProduct(key)
);

export const FOX_ESS_BATTERY_OPTIONS = [
  {
    id: 'ep6',
    name: 'Fox ESS EP6',
    capacity_kwh: 5.76,
    max_units: 4,
    compatibleWith: ['H1', 'KH', 'H3', 'H3-Pro'],
    energy_range: '5.76-23.04 kWh',
    note: 'Scalable high-voltage storage for H1, KH, H3 and H3-Pro systems.',
  },
  {
    id: 'ep11',
    name: 'Fox ESS EP11',
    capacity_kwh: 10.36,
    max_units: 4,
    compatibleWith: ['H1', 'KH', 'H3', 'H3-Pro'],
    energy_range: '10.36-41.60 kWh',
    note: 'Scalable high-voltage storage for larger residential and light commercial systems.',
  },
];

export const FOX_ESS_BATTERY_BRAND_DESCRIPTION =
  'Hybrid & AC inverters (5 kW, 8 kW, 10.5 kW, 30 kW) plus current EP6 and EP11 high-voltage storage batteries for scalable backup and self-consumption.';

export const normalizeFoxEssBatteryProducts = (products = []) => {
  const incomingProducts = Array.isArray(products) ? products : [];
  const existing = new Map();

  incomingProducts.forEach((product) => {
    if (!isFoxEssBattery(product)) return;
    const key = getFoxEssBatteryKey(product);
    if (key) {
      existing.set(key, product);
    }
  });

  const normalizedFoxBatteries = FOX_ESS_BATTERY_KEYS.map((key) =>
    buildFoxEssBatteryProduct(key, existing.get(key))
  );

  const productsWithoutFoxBatteries = incomingProducts.filter((product) => !isFoxEssBattery(product));
  const lastFoxEssIndex = productsWithoutFoxBatteries.reduce(
    (lastIndex, product, index) =>
      String(product?.brand || '').toLowerCase() === 'foxess' ? index : lastIndex,
    -1
  );
  const insertAt = lastFoxEssIndex + 1;

  return [
    ...productsWithoutFoxBatteries.slice(0, insertAt),
    ...normalizedFoxBatteries,
    ...productsWithoutFoxBatteries.slice(insertAt),
  ];
};
